<template>
  <div class="expanded-filter-component" ref="panelRef">
    <!-- Header: button collapses back to hover mode (WCAG 4.1.2) -->
    <button
      type="button"
      class="filter-header"
      aria-label="Collapse filter panel"
      @click="$emit('close')"
    >
      <div class="header-content">
        <span class="header-title">All Filters</span>
        <!-- Decorative icon hidden from AT (WCAG 1.1.1) -->
        <div class="filter-icon" aria-hidden="true">
          <img src="/images/icon/filter-icon.svg" alt="" />
        </div>
      </div>
    </button>

    <!-- Navigation/Content -->
    <div class="filter-content">
      <!-- Free Section -->
      <div class="filter-section">
        <div
          class="option-item standalone"
          @click="onStandaloneRowClick('free', $event)"
        >
          <span class="option-label">Free</span>
          <label class="checkbox-container">
            <input
              type="checkbox"
              role="checkbox"
              v-model="selectedFree"
              @change="onFreeChange"
              class="filter-checkbox"
              aria-label="Free"
            />
            <span class="checkbox-visual" aria-hidden="true"></span>
          </label>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Popular Section -->
      <div class="filter-section">
        <div
          class="option-item standalone"
          @click="onStandaloneRowClick('popular', $event)"
        >
          <span class="option-label">Popular</span>
          <label class="checkbox-container">
            <input
              type="checkbox"
              role="checkbox"
              v-model="selectedPopular"
              @change="onPopularChange"
              class="filter-checkbox"
              aria-label="Popular"
            />
            <span class="checkbox-visual" aria-hidden="true"></span>
          </label>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Grade Section -->
      <div class="filter-section">
        <!-- WCAG 4.1.2: section toggle is a button -->
        <button
          type="button"
          class="section-header"
          :aria-expanded="expandedSections.grade"
          aria-controls="section-grade"
          @click="toggleSection('grade')"
        >
          <span class="section-title">Grade</span>
          <!-- Decorative chevron hidden from AT (WCAG 1.1.1) -->
          <div
            class="section-arrow"
            :class="{ expanded: expandedSections.grade }"
            aria-hidden="true"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </button>

        <div
          id="section-grade"
          v-if="expandedSections.grade"
          class="section-content"
        >
          <div
            v-for="option in gradeOptions"
            :key="option.value"
            class="option-item"
            @click="onOptionRowClick('grade', option.value, $event)"
          >
            <span class="option-label">{{ option.title }}</span>
            <!-- WCAG 1.3.1: label wraps checkbox so click area is correct -->
            <label class="checkbox-container">
              <input
                type="checkbox"
                role="checkbox"
                :value="option.value"
                v-model="selectedGrades"
                @change="onGradeChange(option.value, $event.target.checked)"
                class="filter-checkbox"
                :aria-label="option.title"
              />
              <span class="checkbox-visual" aria-hidden="true"></span>
            </label>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Subject Section -->
      <div class="filter-section">
        <button
          type="button"
          class="section-header"
          :aria-expanded="expandedSections.subject"
          aria-controls="section-subject"
          @click="toggleSection('subject')"
        >
          <span class="section-title">Subject</span>
          <div
            class="section-arrow"
            :class="{ expanded: expandedSections.subject }"
            aria-hidden="true"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </button>

        <div
          id="section-subject"
          v-if="expandedSections.subject"
          class="section-content"
        >
          <div
            v-for="option in subjectOptions"
            :key="option.value"
            class="option-item"
            @click="onOptionRowClick('subject', option.value, $event)"
          >
            <span class="option-label">{{ option.title }}</span>
            <label class="checkbox-container">
              <input
                type="checkbox"
                role="checkbox"
                :value="option.value"
                v-model="selectedSubjects"
                @change="onSubjectChange(option.value, $event.target.checked)"
                class="filter-checkbox"
                :aria-label="option.title"
              />
              <span class="checkbox-visual" aria-hidden="true"></span>
            </label>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Duration Section -->
      <div class="filter-section">
        <button
          type="button"
          class="section-header"
          :aria-expanded="expandedSections.duration"
          aria-controls="section-duration"
          @click="toggleSection('duration')"
        >
          <span class="section-title">Duration</span>
          <div
            class="section-arrow"
            :class="{ expanded: expandedSections.duration }"
            aria-hidden="true"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </button>

        <div
          id="section-duration"
          v-if="expandedSections.duration"
          class="section-content"
        >
          <div
            v-for="option in durationOptions"
            :key="option.value"
            class="option-item"
            @click="onOptionRowClick('duration', option.value, $event)"
          >
            <span class="option-label">{{ option.title }}</span>
            <label class="checkbox-container">
              <input
                type="checkbox"
                role="checkbox"
                :value="option.value"
                v-model="selectedDuration"
                @change="onDurationChange(option.value, $event.target.checked)"
                class="filter-checkbox"
                :aria-label="option.title"
              />
              <span class="checkbox-visual" aria-hidden="true"></span>
            </label>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Hardware Section -->
      <div class="filter-section">
        <button
          type="button"
          class="section-header"
          :aria-expanded="expandedSections.hardware"
          aria-controls="section-hardware"
          @click="toggleSection('hardware')"
        >
          <span class="section-title">Required Equipment</span>
          <div
            class="section-arrow"
            :class="{ expanded: expandedSections.hardware }"
            aria-hidden="true"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </button>

        <div
          id="section-hardware"
          v-if="expandedSections.hardware"
          class="section-content"
        >
          <div
            v-for="option in hardwareOptions"
            :key="option.value"
            class="option-item"
            @click="onOptionRowClick('hardware', option.value, $event)"
          >
            <span class="option-label">{{ option.title }}</span>
            <label class="checkbox-container">
              <input
                type="checkbox"
                role="checkbox"
                :value="option.value"
                v-model="selectedHardware"
                @change="onHardwareChange(option.value, $event.target.checked)"
                class="filter-checkbox"
                :aria-label="option.title"
              />
              <span class="checkbox-visual" aria-hidden="true"></span>
            </label>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Tags Section -->
      <div class="filter-section">
        <button
          type="button"
          class="section-header"
          :aria-expanded="expandedSections.tags"
          aria-controls="section-tags"
          @click="toggleSection('tags')"
        >
          <span class="section-title">Tags</span>
          <div
            class="section-arrow"
            :class="{ expanded: expandedSections.tags }"
            aria-hidden="true"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </button>

        <div
          id="section-tags"
          v-if="expandedSections.tags"
          class="section-content"
        >
          <div
            v-for="category in tagCategories"
            :key="category.category"
            class="tag-category"
          >
            <!-- WCAG 4.1.2: category accordion toggle is a button -->
            <button
              type="button"
              class="category-header"
              :aria-expanded="!!expandedTagCategories[category.category]"
              :aria-controls="'tag-cat-' + category.category"
              @click="toggleTagCategory(category.category)"
            >
              <span class="category-title">{{ category.category }}</span>
              <div
                class="category-arrow"
                :class="{ expanded: expandedTagCategories[category.category] }"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </button>

            <div
              v-if="expandedTagCategories[category.category]"
              :id="'tag-cat-' + category.category"
              class="tag-chips"
            >
              <!-- WCAG 4.1.2: tag chips are checkbox widgets with keyboard support -->
              <div
                v-for="tag in getVisibleTags(category)"
                :key="tag.id"
                class="tag-chip"
                role="checkbox"
                :aria-checked="isTagSelected(tag.tag)"
                :tabindex="0"
                :class="{ selected: isTagSelected(tag.tag) }"
                @click="toggleTag(tag)"
                @keydown.space.prevent="toggleTag(tag)"
                @keydown.enter.prevent="toggleTag(tag)"
              >
                {{ tag.tag }}
              </div>

              <!-- WCAG 4.1.2: view-more is a button -->
              <button
                v-if="
                  category.tag.length > 5 && !showMoreTags[category.category]
                "
                type="button"
                class="view-more-button"
                :aria-label="'View more ' + category.category + ' tags'"
                @click="toggleShowMoreTags(category.category)"
              >
                <span class="view-more-text">View More</span>
                <div class="view-more-arrow" aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";

const props = defineProps({
  selectedFilters: {
    type: Array,
    default: () => [],
  },
  initialFilters: {
    type: Object,
    default: () => ({
      subjects: [],
      grades: [],
      focus: [],
      hardware: [],
      duration: [],
      tags: [],
      sort: "newest",
      locale: "US",
      popular: false,
      free: false,
    }),
  },
  locale: {
    type: String,
    default: "US",
  },
  subjectOptions: {
    type: Array,
    default: () => [],
  },
  gradeOptions: {
    type: Array,
    default: () => [],
  },
  hardwareOptions: {
    type: Array,
    default: () => [],
  },
  durationOptions: {
    type: Array,
    default: () => [],
  },
  tagOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["filtersChanged", "close"]);

// Section expansion state
const expandedSections = ref({
  grade: true,
  subject: true,
  duration: true,
  hardware: true,
  tags: true,
});

const expandedTagCategories = ref({});
const showMoreTags = ref({});

// Selected values
const selectedGrades = ref([]);
const selectedSubjects = ref([]);
const selectedDuration = ref([]);
const selectedHardware = ref([]);
const selectedTags = ref([]);
const selectedFree = ref(false);
const selectedPopular = ref(false);

// Computed tag categories
const tagCategories = computed(() => {
  return props.tagOptions.map((category) => ({
    ...category,
    tag: [...category.tag].sort((a, b) => b.count - a.count),
  }));
});

// Initialize tag category states
watch(
  () => props.tagOptions,
  (newOptions) => {
    if (newOptions && newOptions.length > 0) {
      const expandedCats = {};
      const showMore = {};
      newOptions.forEach((category) => {
        // Collapse tag category accordions by default
        expandedCats[category.category] = false;
        showMore[category.category] = false;
      });
      expandedTagCategories.value = expandedCats;
      showMoreTags.value = showMore;
    }
  },
  { immediate: true }
);

function normalizeInitialSelection(list, options) {
  if (!Array.isArray(list)) return [];
  const nonAll = (options || [])
    .filter((opt) => !opt.disabled && opt.value !== "all")
    .map((opt) => opt.value);
  const set = new Set(list);
  const hasAll = set.has("all");
  const selectedNonAll = list.filter((v) => v !== "all");
  if (hasAll) return [...nonAll, "all"];
  if (selectedNonAll.length === nonAll.length && nonAll.length > 0)
    return [...selectedNonAll, "all"];
  return [...selectedNonAll];
}

// Watch for initial filters and apply them
watch(
  () => props.initialFilters,
  (newFilters) => {
    if (newFilters) {
      selectedGrades.value = normalizeInitialSelection(
        newFilters.grades || [],
        props.gradeOptions
      );
      selectedSubjects.value = normalizeInitialSelection(
        newFilters.subjects || [],
        props.subjectOptions
      );
      selectedDuration.value = normalizeInitialSelection(
        newFilters.duration || [],
        props.durationOptions
      );
      selectedHardware.value = normalizeInitialSelection(
        newFilters.hardware || [],
        props.hardwareOptions
      );
      selectedTags.value = newFilters.tags || [];
      selectedFree.value = newFilters.free || false;
      selectedPopular.value = newFilters.popular || false;
    }
  },
  { immediate: true, deep: true }
);

function reconcileAllBehavior(selectedRef, options, changedValue, isChecked) {
  const nonAll = (options || [])
    .filter((opt) => !opt.disabled && opt.value !== "all")
    .map((opt) => opt.value);
  const hasAll = selectedRef.value.includes("all");
  const selectedNonAll = selectedRef.value.filter((v) => v !== "all");

  if (changedValue === "all") {
    if (isChecked) {
      selectedRef.value = [...nonAll, "all"];
    } else {
      selectedRef.value = [];
    }
    return;
  }

  if (selectedNonAll.length === nonAll.length) {
    if (!hasAll) selectedRef.value = [...selectedNonAll, "all"];
  } else {
    if (hasAll) selectedRef.value = [...selectedNonAll];
  }
}

// Toggle expand/collapse for sections and tag categories
function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section];
}

function toggleTagCategory(category) {
  expandedTagCategories.value[category] =
    !expandedTagCategories.value[category];
}

function toggleShowMoreTags(category) {
  showMoreTags.value[category] = !showMoreTags.value[category];
}

const onGradeChange = (changedValue, isChecked) => {
  reconcileAllBehavior(
    selectedGrades,
    props.gradeOptions,
    changedValue,
    isChecked
  );
  emitFilters();
};

const onSubjectChange = (changedValue, isChecked) => {
  reconcileAllBehavior(
    selectedSubjects,
    props.subjectOptions,
    changedValue,
    isChecked
  );
  emitFilters();
};

const onDurationChange = (changedValue, isChecked) => {
  reconcileAllBehavior(
    selectedDuration,
    props.durationOptions,
    changedValue,
    isChecked
  );
  emitFilters();
};

const onHardwareChange = (changedValue, isChecked) => {
  reconcileAllBehavior(
    selectedHardware,
    props.hardwareOptions,
    changedValue,
    isChecked
  );
  emitFilters();
};

const onFreeChange = () => {
  emitFilters();
};

const onPopularChange = () => {
  emitFilters();
};

// Helper function to check if current device is mobile
const isMobile = () => {
  return window.innerWidth <= 768;
};

// Handle row click for multi-select filters (grade, subject, duration, hardware)
const onOptionRowClick = (filterType, optionValue, event) => {
  // Only handle on mobile devices
  if (!isMobile()) return;

  // Prevent double-triggering if clicked on checkbox itself
  if (
    event.target.type === "checkbox" ||
    event.target.closest(".checkbox-container")
  ) {
    return;
  }

  let selectedRef;
  let changeHandler;

  switch (filterType) {
    case "grade":
      selectedRef = selectedGrades;
      changeHandler = onGradeChange;
      break;
    case "subject":
      selectedRef = selectedSubjects;
      changeHandler = onSubjectChange;
      break;
    case "duration":
      selectedRef = selectedDuration;
      changeHandler = onDurationChange;
      break;
    case "hardware":
      selectedRef = selectedHardware;
      changeHandler = onHardwareChange;
      break;
    default:
      return;
  }

  const isCurrentlyChecked = selectedRef.value.includes(optionValue);

  if (isCurrentlyChecked) {
    // Remove from selection
    const index = selectedRef.value.indexOf(optionValue);
    if (index > -1) {
      selectedRef.value.splice(index, 1);
    }
  } else {
    // Add to selection
    selectedRef.value.push(optionValue);
  }

  changeHandler(optionValue, !isCurrentlyChecked);
};

// Handle row click for standalone checkboxes (free, popular)
const onStandaloneRowClick = (filterType, event) => {
  // Only handle on mobile devices
  if (!isMobile()) return;

  // Prevent double-triggering if clicked on checkbox itself
  if (
    event.target.type === "checkbox" ||
    event.target.closest(".checkbox-container")
  ) {
    return;
  }

  if (filterType === "free") {
    selectedFree.value = !selectedFree.value;
    onFreeChange();
  } else if (filterType === "popular") {
    selectedPopular.value = !selectedPopular.value;
    onPopularChange();
  }
};

// Added: functions used by the template for tags
function getVisibleTags(category) {
  const isShowingMore = showMoreTags.value[category.category];
  return isShowingMore ? category.tag : category.tag.slice(0, 5);
}

function isTagSelected(tagName) {
  return selectedTags.value.includes(tagName);
}

function toggleTag(tag) {
  const tagName = tag.tag;
  const idx = selectedTags.value.indexOf(tagName);
  if (idx === -1) {
    selectedTags.value.push(tagName);
  } else {
    selectedTags.value.splice(idx, 1);
  }
  emitFilters();
}

const emitFilters = () => {
  const filters = {
    subjects: props.subjectOptions.filter((option) =>
      selectedSubjects.value.includes(option.value)
    ),
    grades: props.gradeOptions.filter((option) =>
      selectedGrades.value.includes(option.value)
    ),
    focus: [],
    hardwareAssociated: props.hardwareOptions.filter((option) =>
      selectedHardware.value.includes(option.value)
    ),
    hardware: props.hardwareOptions.filter((option) =>
      selectedHardware.value.includes(option.value)
    ),
    duration: props.durationOptions.filter((option) =>
      selectedDuration.value.includes(option.value)
    ),
    tags: getSelectedTagObjects(),
    popular: selectedPopular.value,
    free: selectedFree.value,
  };

  emit("filtersChanged", filters);
};

const getSelectedTagObjects = () => {
  const selectedTagObjects = [];
  tagCategories.value.forEach((category) => {
    if (category.tag) {
      category.tag.forEach((tag) => {
        if (selectedTags.value.includes(tag.tag)) {
          selectedTagObjects.push({
            id: tag.id,
            title: tag.tag,
            value: tag.tag,
            count: tag.count,
            category: category.category,
          });
        }
      });
    }
  });
  return selectedTagObjects;
};
</script>

<style scoped>
.expanded-filter-component {
  position: relative;
  box-shadow:
    0px 6px 8px 4px rgba(0, 0, 0, 0.15),
    0px 2px 3px 0px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  width: 100%;
  transition: width 0.3s ease-in-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Header section styling */
.filter-header {
  /* #56b693 (--green-light-2) gave #FFFFFF only 2.47:1 — fails WCAG AA.
     #2d7a58 (same green hue, darker) gives #FFFFFF 5.21:1 — passes AA. */
  background-color: #29674c;
  padding: 8px;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

.filter-header:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: -3px;
}

.filter-header:hover {
  background-color: #266b4e; /* proportionally darkened hover to match new base */
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
}

.header-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #ffffff;
  letter-spacing: 0.024px;
}

.filter-icon {
  color: #ffffff;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Content section styling */
.filter-content {
  background-color: #ffffff;
  padding: 8px;
  border-radius: 0 0 12px 12px;
}

.filter-section {
  margin-bottom: 2px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

.section-header:focus-visible {
  outline: 3px solid var(--primary-green, #00925e);
  outline-offset: 2px;
}

.section-header:hover {
  background-color: #f9fafb;
}

.section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.section-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.section-arrow.expanded {
  transform: rotate(180deg);
}

.section-arrow svg {
  width: 18px;
  height: 18px;
}

.section-content {
  padding-left: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  height: 48px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
  font-size: var(--fs-big);
}

.option-item.standalone {
  padding-left: 16px;
}

.option-item:hover {
  background-color: #f9fafb;
}

.option-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.checkbox-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.filter-checkbox {
  opacity: 0;
  position: absolute;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-visual {
  width: 24px;
  height: 24px;
  background-color: #ffffff;
  border: 2px solid #cccccc;
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
}

.filter-checkbox:checked ~ .checkbox-visual {
  background-color: #00925e;
  border-color: #00925e;
}

.checkbox-visual:after {
  content: "";
  position: absolute;
  display: none;
}

.filter-checkbox:checked ~ .checkbox-visual:after {
  display: block;
}

.checkbox-visual:after {
  left: 7px;
  top: 3px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
}

.section-divider {
  height: 1px;
  background-color: #e5e7eb;
  margin: 0 16px;
}

/* Tags specific styling */
.tag-category {
  margin-bottom: 8px;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 32px 10px 16px;
  height: 48px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

.category-header:focus-visible {
  outline: 3px solid var(--primary-green, #00925e);
  outline-offset: 2px;
}

.category-header:hover {
  background-color: #f9fafb;
}

.category-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.category-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.category-arrow.expanded {
  transform: rotate(180deg);
}

.category-arrow svg {
  width: 18px;
  height: 18px;
}

.tag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 48px 8px 32px;
  max-width: 230px;
}

.tag-chip {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #dfe2e7;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #40444b;
  letter-spacing: 0.035px;
  user-select: none;
}

.tag-chip:hover {
  background-color: #f9fafb;
  border-color: #00925e;
}

.tag-chip.selected {
  background-color: #00925e;
  border-color: #00925e;
  color: #ffffff;
}

.view-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 12px;
  margin: 8px 0;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
}

.view-more-button:hover {
  background-color: #f9fafb;
}

.view-more-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.view-more-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-more-arrow svg {
  width: 18px;
  height: 18px;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .section-title,
  .option-label,
  .category-title {
    font-size: var(--fs-small);
  }

  .view-more-button {
    padding: 8px 12px;
    max-height: 40px;
    margin: 0;
  }

  .view-more-text {
    font-size: var(--fs-small);
  }

  .tag-chips {
    max-width: 200px;
    padding-right: 16px;
  }

  .tag-chip {
    max-height: 40px;
  }

  /* Make option items fully clickable on mobile */
  .option-item {
    cursor: pointer;
  }
}
</style>
